import debounce from 'lodash/debounce'
import fetch from 'axios'
import localforage from 'localforage'
import { store } from '@remote/store'

export interface IReporterConfig {
  /** 上报周期，每一个周期结尾将当前数据池内容上报 */
  period: number
  /** 上报容量，达到最大容量时将数据上报 */
  max: number
  /** 最大连续上报异常次数 */
  maxErrorCount: number
}

export interface IReportMessage {
  topic: 'report_info' | 'report_error'
  traceId: string
  platform: 'pc' | 'h5' | 'an' | 'ios'
  biz: 'hezi'
  eventId: string
  timestamp: number
  userId: string | number
  deviceId: string
  /** 额外信息，JSON字符串 */
  extData: string
}

class Reporter {
  private config: IReporterConfig = { period: 0, max: 20, maxErrorCount: 5 }
  private timer: any = null
  // 持续report后的错误次数
  private ongoingErrorCount = 0
  private buffers: IReportMessage[] = []
  private lock4cache = false

  constructor (config?: Partial<IReporterConfig>) {
    if (config) {
      const {
        period = this.config.period,
        max = this.config.max,
        maxErrorCount = this.config.maxErrorCount,
      } = config
      this.config = { period, max, maxErrorCount }
    }
    this.send = this.send.bind(this)
    this.push = this.push.bind(this)
    this.setConfig = this.setConfig.bind(this)
    this.destroy = this.destroy.bind(this)
    this.updateReporterCache = debounce(this.updateReporterCache, 200, {
      // leading: true,
      trailing: true,
    })

    this.resetTimer()
    this.recoverLostReport()
  }

  /** 将buffers内容备份到缓存中（需要在每次buffers变更后执行） */
  private updateReporterCache (buffers = this.buffers) {
    if (this.lock4cache) {
      return
    }
    this.lock4cache = true
    localforage.setItem('report_cache', buffers).finally(() => {
      this.lock4cache = false
    })
  }

  /** 从本地缓存数据取出上次未完成上报的数据，并进行上报 */
  private recoverLostReport () {
    localforage.getItem('report_cache').then(data => {
      if (data) {
        if (Array.isArray(data)) {
          this.buffers.unshift(...data)
          this.send()
        }
      }
    })
  }

  /** 重置周期上报定时器 */
  private resetTimer () {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
    const period = this.config.period
    // 根据上报周期定时上报
    if (period > 0) {
      this.timer = setInterval(() => {
        this.send()
      }, period)
    }
  }

  /** 将缓存区内容统一上报 */
  private send () {
    if (this.ongoingErrorCount >= this.config.maxErrorCount) {
      return
    }
    const data = this.buffers.slice()
    this.buffers = []
    this.updateReporterCache()
    if (!data.length) return
    const url = 'https://api-report-hezi.xunlei.com/api/v2/log/report'
    fetch.post(url, data).then(() => {
      this.ongoingErrorCount = 0
    }).catch((err) => {
      this.ongoingErrorCount += 1
      console.error(err)
      this.buffers.unshift(...data)
      this.updateReporterCache()
    })
  }

  /** 修改上报配置 */
  public setConfig (config: Partial<IReporterConfig>) {
    // 修改配置先清空数据
    this.send()
    const prevPeriod = this.config.period
    const { period = prevPeriod } = config
    this.config = {
      ...this.config,
      ...config,
    }
    // 修改了上报周期
    if (period !== prevPeriod) this.resetTimer()
  }

  /** 数据上报对外暴露接口，将上报数据写入缓存区 */
  public push (report?: IReportMessage) {
    if (report) {
      this.buffers.push({ ...report, deviceId: store.state?.deviceInfoWatchData.target })
      this.updateReporterCache()
    }
    if (this.buffers.length >= this.config.max) {
      // 达到能够容纳的最大值，发送并清空数据
      this.send()
    }
  }

  /** 销毁操作，在APP结束前执行将剩下的数据上报 */
  public destroy () {
    this.send()
    if (this.timer) {
      clearInterval(this.timer)
    }
    this.timer = null
  }
}

export default new Reporter({
  /** 5分钟上报一次 */
  period: 1 * 60 * 1000,
  /** 数据量达到20时进行上报 */
  max: 10,
})
